import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, HostBinding } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { navigationButtons } from 'layout/navigation-buttons';
import { groupBy } from 'lodash';
import { AccessCondition, AccessString, AuthService, PermissionsHelper } from 'providers/auth.service';
import { ConfigService } from 'providers/config.service';
import { EnvironmentService } from 'providers/environment.service';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FeatureFlagService } from '../../services/feature-flags';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';

interface NavigationButtonSection {
    sectionName: string;
    buttons: typeof navigationButtons;
    active: boolean;
}

@Component({
    selector: 'ce-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class SidenavComponent {
    @Input() mode: 'hidden' | 'collapsed' | 'expanded' = 'collapsed';
    @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    public navigationButtonSections: NavigationButtonSection[] = [];
    private featureFlagState: { [key: string]: boolean } = {};

    private permissionsHelper: PermissionsHelper;
    private baseUrl!: string;

    isChargEye: boolean = false;
    skipDialog = false;
    betaLink!: string;

    isMyKempower$ = this.licenseInfoService.isMyKempower$;

    constructor(
        private auth: AuthService,
        private cdRef: ChangeDetectorRef,
        private featureFlagService: FeatureFlagService,
        private router: Router,
        private environmentService: EnvironmentService,
        private licenseInfoService: LicenseInfoService,
        private dialog: MatDialog,
        private config: ConfigService
    ) {
        this.skipDialog = localStorage.getItem('hide-beta-update-dialog') === 'true';
        this.baseUrl = this.config?.config.apiBaseUrl || '';
        this.betaLink = `https://${this.baseUrl}/2.0/?=beta:true`;
        this.initButtons();

        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = `theme-${origin}`;
                this.isChargEye = origin === 'chargeye';
                this.cdRef.markForCheck();
            });

        this.router.events
            .pipe(
                filter((ev) => ev instanceof NavigationEnd),
                untilDestroyed(this)
            )
            .subscribe(() => this.refreshButtons());
    }

    private initButtons(): void {
        combineLatest([this.auth.authenticated$, this.featureFlagService.getFlagState$(), this.auth.permissions$])
            .pipe(untilDestroyed(this))
            .subscribe(([, flagState, helper]) => {
                this.featureFlagState = flagState;

                this.permissionsHelper = helper;
                this.refreshButtons();
            });
    }

    private refreshButtons(): void {
        this.navigationButtonSections = Object.values(groupBy(navigationButtons, (n) => n.section))
            .map((buttons) => {
                const filteredButtons: typeof navigationButtons = [];
                for (const b of buttons) {
                    // check duplicate links
                    const linkExisted = filteredButtons.some((_b) => _b.routerLink === b.routerLink);
                    if (linkExisted) {
                        continue;
                    }

                    // tenant id filter
                    if (
                        b.tenantIds &&
                        this.auth.profile?.tenantId &&
                        !b.tenantIds.includes(this.auth.profile.tenantId)
                    ) {
                        // don't add this button, for a different tenant id
                        continue;
                    }

                    // check access
                    if (b.access) {
                        const condition: AccessCondition = b.accessCondition || 'AND';
                        const accessString: AccessString = b.access;

                        if (!this.permissionsHelper.hasAccess(accessString, condition)) {
                            continue;
                        }
                    }

                    // feature flag filter
                    if (b.featureFlag && !this.featureFlagState[b.featureFlag]) {
                        continue;
                    }

                    // sys admin filter - override access filter
                    if (b.sysAdminOnly && !this.auth.getPermissions().isSystemAdmin) {
                        continue;
                    }

                    filteredButtons.push(b);
                }

                return filteredButtons?.length > 0
                    ? ({
                          buttons: filteredButtons,
                          sectionName: buttons[0].section,
                          active: buttons.some((b) => this.router.isActive(b.routerLink, false)),
                      } as NavigationButtonSection)
                    : undefined;
            })
            .filter(Boolean);
        this.cdRef.markForCheck();
    }

    navigateToBeta(redirect?: boolean | undefined): void {
        if (redirect) {
            window.location.href = this.betaLink;
            return;
        }
        this.openDialog();
    }

    openDialog(): void {
        const skipDialog = localStorage.getItem('hide-beta-update-dialog') === 'true';
        if (skipDialog) {
            this.navigateToBeta(true);
            return;
        }
        const dialogRef = this.dialog.open(BetaNotificationDialog);

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.doNotShow) {
                localStorage.setItem('hide-beta-update-dialog', 'true');
            }
            if (result?.gotoBeta) {
                this.navigateToBeta(true);
            }
        });
    }
}

@Component({
    selector: 'dialog-overview-example-dialog',
    styleUrls: ['./ce-intro-dialog.scss'],
    template: `
        <div class="CeIntroDialog">
            <h1 class="CeIntroDialog-header">Try ChargEye beta</h1>
            <div class="CeIntroDialog-content">
                <p>
                    We're working on a brand new version of ChargEye. While most features still live in ChargEye
                    classic, you can already explore the new charging network view and a few other early features.
                </p>
                <mat-checkbox [(ngModel)]="doNotShowCheck">Do not show this message again</mat-checkbox>

                <section class="example-section"></section>
            </div>
            <div class="CeIntroDialog-footer">
                <button class="CeIntroDialog-button" (click)="dialogAction(false)">Stay here</button>
                <button class="CeIntroDialog-button CeIntroDialog-button--primary" (click)="dialogAction(true)">
                    Continue to ChargEye beta
                </button>
            </div>
        </div>
    `,
})
export class BetaNotificationDialog {
    constructor(public dialogRef: MatDialogRef<BetaNotificationDialog>) {}
    doNotShowCheck: false;

    dialogAction(goToBeta: boolean): void {
        this.dialogRef.close({ gotoBeta: goToBeta, doNotShow: this.doNotShowCheck });
    }
}
